import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { countryData, PhoneField, type PhoneFieldInterface } from '@sunwebgroup/uds-ui/fields/phone-field'
import { TextField } from '@sunwebgroup/uds-ui/fields/text-field'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { useCustomerErrorMessage } from '@/scopes/checkout/components/EngineError/hooks/useCustomerErrorMessage'
import { mapPhoneNumberToString } from '@/scopes/checkout/infrastructure/api/mapPhoneNumber'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createErrorEvent } from '../../domain/analytics'
import { useEngineErrorContactInfo } from '../../domain/hooks'
import { CheckoutError } from '../../domain/types'
import { emailPattern, namePattern, useSmartFieldStatus } from '../helpers/Validations'
import styles from './EngineError.module.css'
import { useEngineErrorContactInfoStore } from './store'
import { Dialog } from '@sunwebgroup/uds-ui/dialog'
import { Divider } from '@sunwebgroup/uds-ui/divider'

interface FormValues {
  name: string
  email: string
  phone: PhoneFieldInterface
}

export interface EngineErrorProps {
  error: CheckoutError | undefined
}

// EngineError retrieves error message from props.
// This allows to render the error from different points
// such as the booking draft query or the ryanair session data query
export function EngineError({ error }: EngineErrorProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const hasBeenSent = React.useRef(false)
  const [isChecking, setIsChecking] = React.useState(false)
  const [isSent, setIsSet] = React.useState(false)
  const [selectedCountryMobile, setSelectedCountryMobile] = React.useState(countryData[0])

  const engineErrorConfig = useCMSPageField('Checkout.engineError')

  const { setValue, formState, control, handleSubmit, getFieldState, watch } = useForm<FormValues>({ mode: 'all' })
  const errorsTyped = formState.errors as Record<keyof FormValues, { message: string }>

  const { sendEngineErrorContactInfo } = useEngineErrorContactInfo()

  const engineErrorStore = useEngineErrorContactInfoStore(state => state.result)
  const setBookingInfoError = useEngineErrorContactInfoStore(state => state.setEngineErrorContactInfoResponse)

  React.useEffect(() => {
    if (engineErrorStore === true) {
      setIsChecking(false)
      setIsSet(true)
    }
    else if (engineErrorStore === false) {
      setIsChecking(false)
    }
  }, [engineErrorStore])

  const onSubmit = (result: FormValues) => {
    setIsChecking(true)
    sendEngineErrorContactInfo({
      customerName: result.name,
      customerEmail: result.email,
      customerPhone: result.phone ? mapPhoneNumberToString(result.phone) : '',
    })
  }

  const filteredCountry = engineErrorConfig?.PhoneInputSettings
    ? engineErrorConfig.PhoneInputSettings.map(setting =>
        countryData.find(country => country.isoCode === setting.IsoCode),
      ).filter((country): country is NonNullable<typeof country> => Boolean(country))
    : countryData

  const nameFieldState = getFieldState(`name`, formState)
  const watchedNameFieldValue = watch(`name`) || ''
  const statusNameFieldValue = useSmartFieldStatus({
    value: watchedNameFieldValue,
    error: errorsTyped.name,
    isDirty: nameFieldState?.isDirty ?? false,
    fieldLengthValidation: 0,
    isSubmitting: formState?.isSubmitting ?? false,
    matchPattern: new RegExp(namePattern).test(watchedNameFieldValue),
  })

  const phoneFieldState = getFieldState(`phone`, formState)
  const watchedPhoneNumberFieldValue = watch(`phone.phoneNumber`) || ''
  const statusPhoneFieldName = useSmartFieldStatus({
    value: watchedPhoneNumberFieldValue,
    error: errorsTyped.phone,
    isDirty: phoneFieldState?.isDirty ?? false,
    fieldLengthValidation: 0,
    isSubmitting: formState?.isSubmitting ?? false,
    matchPattern: new RegExp(selectedCountryMobile.pattern).test(watchedPhoneNumberFieldValue),
  })

  const eMailFieldState = getFieldState(`email`, formState)
  const watchedeMailFieldValue = watch(`email`) || ''
  const statuseMailFieldName = useSmartFieldStatus({
    value: watchedeMailFieldValue,
    error: errorsTyped.email,
    isDirty: eMailFieldState?.isDirty ?? false,
    fieldLengthValidation: 0,
    isSubmitting: formState?.isSubmitting ?? false,
    matchPattern: new RegExp(emailPattern).test(watchedeMailFieldValue),
  })

  React.useEffect(() => {
    if (hasBeenSent.current)
      return
    sendEvent(createErrorEvent({ description: error?.message ?? '' }))
    hasBeenSent.current = true
  }, [sendEvent, hasBeenSent, error?.message])

  const customerErrorMessage = useCustomerErrorMessage(error, engineErrorConfig)

  return (
    <div className={styles.wrapper}>
      <CheckoutSection title="">
        {(engineErrorStore === undefined || engineErrorStore === false) && (
          <div className={styles.errorMessageWrapper}>
            <Dialog
              className={styles.dialog}
              type='alert'
              icon='ExclamationInTriangle'
              text={intl.formatMessage({ id: 'checkout_steps_engine_error_title' })}
              supportingText={intl.formatMessage({ id: 'checkout_steps_engine_error_subtitle' })}
            />
            <Divider/>
            {engineErrorConfig && (
              <Typography as="section" dangerouslySetInnerHTML={{ __html: customerErrorMessage }} />
            )}
            {engineErrorStore === false && (
              <>
                <Banner hasIcon color="warning" icon="ExclamationInTriangle" size="M" >
                  <div className={styles.errorContactWrapper}>
                    <Banner.TextsWrapper>
                      <Banner.Text>{intl.formatMessage({ id: 'checkout_steps_engine_contact_sent_nok' })}</Banner.Text>
                      <Banner.SupportingText>{engineErrorConfig?.ContactFormResponseNok}</Banner.SupportingText>
                    </Banner.TextsWrapper>
                    <Button
                      variant="primary"
                      size="S"
                      onClick={() => {
                        setBookingInfoError(undefined)
                      }}
                    >
                      {intl.formatMessage({ id: 'checkout_steps_engine_contact_sent_retry' })}
                    </Button>
                  </div>
                </Banner>
              </>
            )}
            <form className={styles.formWrapper} onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: namePattern,
                    message: intl.formatMessage({ id: 'checkout_steps_engine_error_name_invalid' }),
                  },
                }}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={intl.formatMessage({ id: 'checkout_steps_engine_error_name' })}
                    status={statusNameFieldValue}
                    feedbackText={
                      errorsTyped.name?.message
                      || intl.formatMessage({ id: 'checkout_steps_engine_error_name_invalid' })
                    }
                    onChange={field.onChange}
                    onBlur={(e) => {
                      field.onBlur()
                      const newValue = e.target.value
                      setValue('name', newValue, {
                        shouldValidate: true,
                      })
                    }}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: emailPattern,
                    message: intl.formatMessage({ id: 'checkout_steps_engine_error_mail_invalid' }),
                  },
                }}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    type="email"
                    status={statuseMailFieldName}
                    feedbackText={intl.formatMessage({ id: 'checkout_steps_engine_error_mail_invalid' })}
                    label={intl.formatMessage({ id: 'checkout_steps_engine_error_mail' })}
                    {...field}
                    onChange={field.onChange}
                    onBlur={(e) => {
                      field.onBlur()
                      const newValue = e.target.value
                      setValue('email', newValue, {
                        shouldValidate: true,
                      })
                    }}
                  />
                )}
              />
              <div>
                <Typography>{intl.formatMessage({ id: 'checkout_steps_engine_error_phone' })}</Typography>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      const phoneNumberWithoutPrefix = value?.phoneNumber
                      const phonePattern = new RegExp(selectedCountryMobile.pattern)
                      return phonePattern.test(phoneNumberWithoutPrefix)
                    },
                  }}
                  render={({ field }) => (
                    <PhoneField
                      {...field}
                      countries={filteredCountry}
                      id="phone"
                      value={field.value?.phoneNumber || ''}
                      defaultCountryCode={field.value?.countryCode ?? selectedCountryMobile.code}
                      status={statusPhoneFieldName}
                      onPhoneChange={field.onChange}
                      onCountryChange={setSelectedCountryMobile}
                      feedbackText={intl.formatMessage({ id: 'checkout_steps_engine_error_telephone_invalid' })}
                      wrapperClassName={styles.phoneFieldWrapper}
                    />
                  )}
                />
              </div>
              {!isSent && (
                <Button variant="primary" type="submit" disabled={isChecking}>
                  {intl.formatMessage({ id: 'checkout_steps_engine_error_submit_contact_button' })}
                </Button>
              )}
            </form>
          </div>
        )}
        {engineErrorStore === true && (
          <div role="status" aria-live="polite">
             <Dialog
               className={styles.dialogSuccess}
               type="custom"
               icon="CheckMarkInCircle"
               text={intl.formatMessage({ id: 'checkout_steps_engine_contact_sent_ok' })}
               supportingText={engineErrorConfig?.ContactFormResponseOk}
             />
           </div>
        )}
      </CheckoutSection>
    </div>
  )
}
